import { db } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  onSnapshot 
} from 'firebase/firestore';

// Projects Collection
export const projectsCollection = collection(db, 'projects');
export const activitiesCollection = collection(db, 'activities');

// Project CRUD Operations
export const createProject = async (projectData, userId) => {
  try {
    const docRef = await addDoc(projectsCollection, {
      ...projectData,
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'On Track',
      totalTasks: 0,
      notStarted: 0,
      inProgress: 0,
      completed: 0,
      scheduleProgress: '0%',
      scheduleStatus: 'On Track'
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

export const getProjects = async (userId) => {
  try {
    // Try with orderBy first, fallback to simple query if index doesn't exist
    let q = query(
      projectsCollection, 
      where('userId', '==', userId)
    );
    
    try {
      q = query(
        projectsCollection, 
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
    } catch (indexError) {
      console.warn('Using simple query due to missing index:', indexError);
    }
    
    const querySnapshot = await getDocs(q);
    const projects = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort manually if we couldn't use orderBy
    return projects.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
  } catch (error) {
    console.error('Error getting projects:', error);
    throw error;
  }
};

export const getProject = async (projectId) => {
  try {
    const docRef = doc(db, 'projects', projectId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Project not found');
    }
  } catch (error) {
    console.error('Error getting project:', error);
    throw error;
  }
};

export const updateProject = async (projectId, updateData) => {
  try {
    const docRef = doc(db, 'projects', projectId);
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

export const deleteProject = async (projectId) => {
  try {
    const docRef = doc(db, 'projects', projectId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

// Activity CRUD Operations
export const createActivity = async (activityData) => {
  try {
    // Get the current activities count for this project to generate S.No
    const q = query(activitiesCollection, where('projectId', '==', activityData.projectId));
    const querySnapshot = await getDocs(q);
    const sno = querySnapshot.size + 1;

    const docRef = await addDoc(activitiesCollection, {
      ...activityData,
      sno,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Create notification if activity is assigned to someone
    if (activityData.assignedTo) {
      try {
        console.log('Creating notification for activity assignment:', activityData.assignedTo);
        // Find user by display name or email
        const users = await getAllUsers();
        console.log('Available users for activity:', users);
        const assignedUser = users.find(user => 
          user.displayName === activityData.assignedTo || 
          user.email === activityData.assignedTo
        );
        
        console.log('Found assigned user for activity:', assignedUser);
        
        if (assignedUser) {
          // Get project name for notification
          let projectName = 'Unknown Project';
          try {
            const projectDoc = await getDoc(doc(db, 'projects', activityData.projectId));
            if (projectDoc.exists()) {
              projectName = projectDoc.data().name || projectName;
            }
          } catch (error) {
            console.error('Error fetching project name:', error);
          }

          const notificationData = {
            userId: assignedUser.id,
            type: 'activity_assignment',
            title: 'New Activity Assigned',
            message: `You have been assigned to activity: ${activityData.particulars} in project: ${projectName}`,
            activityId: docRef.id,
            activityName: activityData.particulars,
            projectId: activityData.projectId,
            projectName: projectName,
            assignedBy: activityData.createdBy || 'System',
            assignedByPhotoURL: activityData.createdByPhotoURL
          };
          console.log('Creating activity notification with data:', notificationData);
          await createNotification(notificationData);
          console.log('Activity notification created successfully');
        } else {
          console.log('No user found matching for activity:', activityData.assignedTo);
        }
      } catch (notificationError) {
        console.error('Error creating activity assignment notification:', notificationError);
      }
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating activity:', error);
    throw error;
  }
};

export const getActivities = async (projectId) => {
  try {
    const q = query(
      activitiesCollection,
      where('projectId', '==', projectId),
      orderBy('createdAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting activities:', error);
    throw error;
  }
};

export const updateActivity = async (activityId, updateData) => {
  try {
    const docRef = doc(db, 'activities', activityId);
    
    // Auto-update actual end date when status changes to completed
    if (updateData.status === 'Completed' && !updateData.actualEndDate) {
      updateData.actualEndDate = new Date().toISOString().split('T')[0];
    }
    
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating activity:', error);
    throw error;
  }
};

export const deleteActivity = async (activityId) => {
  try {
    const docRef = doc(db, 'activities', activityId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting activity:', error);
    throw error;
  }
};

// Task CRUD Operations
export const createTask = async (taskData) => {
  try {
    const docRef = await addDoc(collection(db, 'tasks'), {
      ...taskData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Create notification if task is assigned to someone
    if (taskData.assignedTo) {
      try {
        console.log('Creating notification for task assignment:', taskData.assignedTo);
        // Find user by display name or email
        const users = await getAllUsers();
        console.log('Available users for task:', users);
        const assignedUser = users.find(user => 
          user.displayName === taskData.assignedTo || 
          user.email === taskData.assignedTo
        );
        
        console.log('Found assigned user for task:', assignedUser);
        
        if (assignedUser) {
          // Get project and activity names for notification
          let projectName = 'Unknown Project';
          let activityName = 'Unknown Activity';
          
          try {
            // Get project name
            const projectDoc = await getDoc(doc(db, 'projects', taskData.projectId));
            if (projectDoc.exists()) {
              projectName = projectDoc.data().name || projectName;
            }
            
            // Get activity name
            const activityDoc = await getDoc(doc(db, 'activities', taskData.activityId));
            if (activityDoc.exists()) {
              activityName = activityDoc.data().particulars || activityName;
            }
          } catch (error) {
            console.error('Error fetching project/activity names:', error);
          }

          const notificationData = {
            userId: assignedUser.id,
            type: 'task_assignment',
            title: 'New Task Assigned',
            message: `You have been assigned to task: ${taskData.name} in activity: ${activityName} (Project: ${projectName})`,
            taskId: docRef.id,
            taskName: taskData.name,
            activityId: taskData.activityId,
            activityName: activityName,
            projectId: taskData.projectId,
            projectName: projectName,
            assignedBy: taskData.createdBy || 'System',
            assignedByPhotoURL: taskData.createdByPhotoURL
          };
          console.log('Creating task notification with data:', notificationData);
          await createNotification(notificationData);
          console.log('Task notification created successfully');
        } else {
          console.log('No user found matching for task:', taskData.assignedTo);
        }
      } catch (notificationError) {
        console.error('Error creating task assignment notification:', notificationError);
      }
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

export const getTasks = async (activityId) => {
  try {
    const q = query(
      collection(db, 'tasks'),
      where('activityId', '==', activityId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting tasks:', error);
    throw error;
  }
};

export const updateTask = async (taskId, updateData) => {
  try {
    const docRef = doc(db, 'tasks', taskId);
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};

export const deleteTask = async (taskId) => {
  try {
    const docRef = doc(db, 'tasks', taskId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};

export const subscribeToTasks = (activityId, callback) => {
  const q = query(
    collection(db, 'tasks'),
    where('activityId', '==', activityId)
  );
  
  return onSnapshot(q, async (querySnapshot) => {
    const tasks = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort tasks by creation date
    const sortedTasks = tasks.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return aTime - bTime;
    });
    
    // Calculate and update activity status based on tasks
    if (tasks.length > 0) {
      const newActivityStatus = calculateActivityStatus(tasks);
      try {
        // Get current activity to check if status actually changed
        const activityDoc = await getDoc(doc(db, 'activities', activityId));
        if (activityDoc.exists()) {
          const currentActivity = activityDoc.data();
          if (currentActivity.status !== newActivityStatus) {
            await updateActivity(activityId, { status: newActivityStatus });
          }
        }
      } catch (error) {
        console.error('Error updating activity status:', error);
      }
    }
    
    callback(sortedTasks);
  }, (error) => {
    console.error('Error in tasks subscription:', error);
    callback([]);
  });
};

// Helper function to calculate activity status based on tasks
const calculateActivityStatus = (tasks) => {
  if (tasks.length === 0) return 'Pending';
  
  const completedTasks = tasks.filter(task => task.status === 'Completed');
  const inProgressTasks = tasks.filter(task => task.status === 'In Progress');
  
  // If all tasks are completed, activity is completed
  if (completedTasks.length === tasks.length) {
    return 'Completed';
  }
  
  // If any task is in progress, activity is in progress
  if (inProgressTasks.length > 0) {
    return 'In Progress';
  }
  
  // Otherwise, activity is pending
  return 'Pending';
};

// Real-time listeners
export const subscribeToProjects = (userId, callback) => {
  // Get all projects first
  const q = query(projectsCollection);
  
  return onSnapshot(q, async (querySnapshot) => {
    const allProjects = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter projects based on user access
    const accessibleProjects = [];
    
    for (const project of allProjects) {
      // Include if user is the project creator
      if (project.userId === userId) {
        accessibleProjects.push(project);
        continue;
      }
      
      // Check if user is assigned to any tasks or activities in this project
      try {
        const hasAccess = await checkUserProjectAccess(project.id, userId);
        if (hasAccess) {
          accessibleProjects.push(project);
        }
      } catch (error) {
        console.error('Error checking project access:', error);
      }
    }
    
    // Sort manually by creation date
    const sortedProjects = accessibleProjects.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
    
    callback(sortedProjects);
  }, (error) => {
    console.error('Error in projects subscription:', error);
    callback([]);
  });
};

export const subscribeToActivities = (projectId, callback) => {
  // Use simple query to avoid index requirement
  const q = query(
    activitiesCollection,
    where('projectId', '==', projectId)
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const activities = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort manually by creation date
    const sortedActivities = activities.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return aTime - bTime; // ascending order
    });
    
    callback(sortedActivities);
  }, (error) => {
    console.error('Error in activities subscription:', error);
    callback([]);
  });
};

// Utility functions
export const calculateProjectStats = (activities) => {
  const totalTasks = activities.length;
  const notStarted = activities.filter(a => a.status === 'Pending').length;
  const inProgress = activities.filter(a => a.status === 'In Progress').length;
  const completed = activities.filter(a => a.status === 'Completed').length;
  
  const scheduleProgress = totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : 0;
  
  return {
    totalTasks,
    notStarted,
    inProgress,
    completed,
    scheduleProgress: `${scheduleProgress}%`,
    scheduleStatus: scheduleProgress >= 90 ? 'Ahead' : scheduleProgress >= 70 ? 'On Track' : 'Behind'
  };
};

// Check if user has access to a project based on task/activity assignments
export const checkUserProjectAccess = async (projectId, userId) => {
  try {
    // Get current user data to match against assignedTo fields
    const users = await getAllUsers();
    const currentUser = users.find(user => user.id === userId);
    
    if (!currentUser) return false;
    
    const userIdentifiers = [
      currentUser.displayName,
      currentUser.email,
      userId
    ].filter(Boolean);
    
    // Check activities in this project
    const activitiesQuery = query(
      collection(db, 'activities'),
      where('projectId', '==', projectId)
    );
    
    const activitiesSnapshot = await getDocs(activitiesQuery);
    
    // Check if user is assigned to any activity
    for (const activityDoc of activitiesSnapshot.docs) {
      const activity = activityDoc.data();
      if (activity.assignedTo && userIdentifiers.includes(activity.assignedTo)) {
        return true;
      }
      
      // Check tasks within this activity
      const tasksQuery = query(
        collection(db, 'tasks'),
        where('activityId', '==', activityDoc.id)
      );
      
      const tasksSnapshot = await getDocs(tasksQuery);
      
      for (const taskDoc of tasksSnapshot.docs) {
        const task = taskDoc.data();
        if (task.assignedTo && userIdentifiers.includes(task.assignedTo)) {
          return true;
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking user project access:', error);
    return false;
  }
};

// User Management Functions
export const getAllUsers = async () => {
  try {
    const q = query(collection(db, 'users'));
    const querySnapshot = await getDocs(q);
    const users = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    console.log('getAllUsers result:', users);
    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    // If users collection doesn't exist, create a fallback using current user
    return [];
  }
};

// Notification CRUD Operations
export const createNotification = async (notificationData) => {
  try {
    console.log('Creating notification with data:', notificationData);
    const docRef = await addDoc(collection(db, 'notifications'), {
      ...notificationData,
      createdAt: serverTimestamp(),
      read: false
    });
    console.log('Notification created successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

export const getUserNotifications = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting notifications:', error);
    throw error;
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    const docRef = doc(db, 'notifications', notificationId);
    await updateDoc(docRef, {
      read: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

export const deleteNotification = async (notificationId) => {
  try {
    const docRef = doc(db, 'notifications', notificationId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

export const subscribeToNotifications = (userId, callback) => {
  console.log('Setting up notifications subscription for userId:', userId);
  
  // Try without orderBy first to avoid index issues
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', userId)
  );
  
  return onSnapshot(q, (querySnapshot) => {
    console.log('Notifications query snapshot received, docs count:', querySnapshot.docs.length);
    const notifications = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort manually by creation date
    const sortedNotifications = notifications.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
    
    console.log('Processed notifications:', sortedNotifications);
    callback(sortedNotifications);
  }, (error) => {
    console.error('Error in notifications subscription:', error);
    callback([]);
  });
};

// Task Messages CRUD Operations
export const createTaskMessage = async (messageData) => {
  try {
    console.log('Creating task message with data:', messageData);
    const docRef = await addDoc(collection(db, 'taskMessages'), {
      ...messageData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    console.log('Task message created successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error creating task message:', error);
    throw error;
  }
};

export const subscribeToTaskMessages = (taskId, callback) => {
  console.log('Setting up task messages subscription for taskId:', taskId);
  
  const q = query(
    collection(db, 'taskMessages'),
    where('taskId', '==', taskId)
  );
  
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    console.log('Task messages query snapshot received, docs count:', querySnapshot.docs.length);
    const messages = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('Raw task messages from Firestore:', messages);
    
    // Sort messages by creation time
    const sortedMessages = messages.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return aTime - bTime;
    });
    
    console.log('Processed task messages:', sortedMessages);
    callback(sortedMessages);
  }, (error) => {
    console.error('Error in task messages subscription:', error);
    callback([]);
  });
  
  return unsubscribe;
};

export const updateTaskMessage = async (messageId, updates) => {
  try {
    console.log('Updating task message:', messageId, updates);
    const messageRef = doc(db, 'taskMessages', messageId);
    await updateDoc(messageRef, {
      ...updates,
      updatedAt: serverTimestamp(),
      isEdited: true
    });
    console.log('Task message updated successfully');
  } catch (error) {
    console.error('Error updating task message:', error);
    throw error;
  }
};

export const deleteTaskMessage = async (messageId) => {
  try {
    console.log('Deleting task message:', messageId);
    await deleteDoc(doc(db, 'taskMessages', messageId));
    console.log('Task message deleted successfully');
  } catch (error) {
    console.error('Error deleting task message:', error);
    throw error;
  }
};

export const addMessageReaction = async (messageId, userId, reaction) => {
  try {
    console.log('Adding reaction to message:', messageId, reaction);
    const messageRef = doc(db, 'taskMessages', messageId);
    const messageDoc = await getDoc(messageRef);
    
    if (messageDoc.exists()) {
      const currentReactions = messageDoc.data().reactions || {};
      const reactionKey = reaction;
      
      if (!currentReactions[reactionKey]) {
        currentReactions[reactionKey] = [];
      }
      
      // Toggle reaction - remove if already exists, add if not
      const userIndex = currentReactions[reactionKey].indexOf(userId);
      if (userIndex > -1) {
        currentReactions[reactionKey].splice(userIndex, 1);
        if (currentReactions[reactionKey].length === 0) {
          delete currentReactions[reactionKey];
        }
      } else {
        currentReactions[reactionKey].push(userId);
      }
      
      await updateDoc(messageRef, {
        reactions: currentReactions,
        updatedAt: serverTimestamp()
      });
    }
    console.log('Reaction updated successfully');
  } catch (error) {
    console.error('Error updating reaction:', error);
    throw error;
  }
};
